import React, { useState, useEffect, useMemo } from "react";
import { Tree, Input, Tag, Spin, Button, Tooltip, Toast } from "@douyinfe/semi-ui";
import { IconSearch, IconGlobe, IconUser, IconCopy, IconList } from "@douyinfe/semi-icons";
import { ApiClient } from "../api/ApiClient";

const apiClient = ApiClient.getDefault();

// ============ 类型 ============

interface TreeNode {
  key: string;
  label: string;
  labelSubText?: string;
  value: string;
  teamId?: number;
  mobile?: string;
  children?: TreeNode[];
}

interface TeamNode {
  key: string;
  label: string;
  value: string;
  children?: TeamNode[];
}

// ============ 工具函数 ============

function countAll(node: any): number {
  if (!node.children || node.children.length === 0) return 1;
  return 1 + node.children.reduce((s: number, c: any) => s + countAll(c), 0);
}

function countUsers(node: any): number {
  if (!node.children || node.children.length === 0) return node._isUser ? 1 : 0;
  return node.children.reduce((s: number, c: any) => s + countUsers(c), 0);
}

function collectBranchKeys(nodes: TreeNode[]): string[] {
  const keys: string[] = [];
  const walk = (list: TreeNode[]) => {
    for (const n of list) {
      if (n.children && n.children.length > 0) { keys.push(n.key); walk(n.children); }
    }
  };
  walk(nodes);
  return keys;
}

function flattenAll(nodes: TreeNode[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  const walk = (list: TreeNode[]) => {
    for (const n of list) {
      if (!map.has(n.key)) map.set(n.key, n);
      if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return Array.from(map.values());
}

function getRoleColor(r: string): string {
  const m: Record<string, string> = {
    系统管理员: "red", 运营主管: "orange", 销售主管: "blue",
    销售: "green", 按揭专员: "purple", 学员: "cyan", 区长: "violet", 监控员: "grey",
  };
  return m[r] || "grey";
}

function getAvatarColor(name: string): string {
  const colors = ["#6366f1","#8b5cf6","#a855f7","#d946ef","#ec4899","#f43f5e","#f97316","#eab308","#22c55e","#14b8a6","#06b6d4","#3b82f6"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function parseLabelSub(text: string) {
  const parts = (text || "").split(" ");
  return { team: parts[0] || "", role: parts.slice(1).join(" ") || "" };
}

// ============ 组件 ============

interface ContactsPanelProps {
  showHeader?: boolean;
  leftWidth?: number;
}

export default function ContactsPanel({ showHeader = true, leftWidth = 440 }: ContactsPanelProps) {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [teamTree, setTeamTree] = useState<TeamNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [expandedLeft, setExpandedLeft] = useState<string[]>([]);
  const [expandedRight, setExpandedRight] = useState<string[]>([]);

  const role = localStorage.getItem("role");
  const username = localStorage.getItem("username");
  const isGlobalRole = ["op_manager", "monitor", "foreground"].includes(role || "");

  const loadData = async (all: boolean) => {
    setLoading(true);
    try {
      const target = all ? "" : isGlobalRole ? "root" : username;
      const [userData, teamData] = await Promise.all([
        apiClient.getTeamMemberTreeAsOptsOfAny(target || "", "all") as unknown as Promise<TreeNode[]>,
        all ? apiClient.getAllTeamAsOpts() as unknown as Promise<TeamNode[]> : apiClient.getTeamAsOpts() as unknown as Promise<TeamNode[]>,
      ]);
      setTreeData(userData);
      setExpandedLeft(collectBranchKeys(userData));
      setTeamTree(teamData);
      setExpandedRight(teamData.map((t) => t.key));
    } catch (e) {
      console.error("加载通讯录失败:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(showAll); }, [showAll]);

  // 所有用户节点（去重）
  const allUsers = useMemo(() => flattenAll(treeData), [treeData]);

  // 搜索过滤
  const filteredUsers = useMemo(() => {
    if (!searchText) return allUsers;
    const t = searchText.toLowerCase();
    return allUsers.filter((u) => {
      const { team, role: r } = parseLabelSub(u.labelSubText || "");
      return u.label?.toLowerCase().includes(t) || team.toLowerCase().includes(t) || r.toLowerCase().includes(t);
    });
  }, [allUsers, searchText]);

  // 右侧部门-人员树（通过 teamId 匹配）
  const rightTreeData = useMemo(() => {
    const teamUserMap = new Map<number, TreeNode[]>();
    filteredUsers.forEach((u) => {
      const tid = u.teamId || 0;
      if (!teamUserMap.has(tid)) teamUserMap.set(tid, []);
      teamUserMap.get(tid)!.push(u);
    });

    const build = (teams: TeamNode[]): TeamNode[] => {
      return teams.map((team) => {
        const tid = Number(team.key);
        const directUsers = teamUserMap.get(tid) || [];
        const childTeams = team.children ? build(team.children) : [];
        if (directUsers.length === 0 && childTeams.length === 0) return null;
        const children: TeamNode[] = [];
        childTeams.forEach((ct) => children.push(ct));
        directUsers.forEach((u) => {
          const { role: r } = parseLabelSub(u.labelSubText || "");
          children.push({ key: "u_" + u.key, label: u.label, value: u.key, labelExt: r, _isUser: true } as any);
        });
        return { ...team, children };
      }).filter(Boolean) as TeamNode[];
    };

    const orphanUsers = teamUserMap.get(0) || [];
    const built = build(teamTree);
    if (orphanUsers.length > 0) {
      built.push({
        key: "_orphan", label: "未分配", value: "_orphan",
        children: orphanUsers.map((u) => {
          const { role: r } = parseLabelSub(u.labelSubText || "");
          return { key: "u_" + u.key, label: u.label, value: u.key, labelExt: r, _isUser: true } as any;
        }),
      });
    }
    return built;
  }, [teamTree, filteredUsers]);

  // 左侧搜索过滤
  const filteredLeftTree = useMemo(() => {
    if (!searchText) return treeData;
    const t = searchText.toLowerCase();
    const match = (node: TreeNode): boolean => {
      const { team, role: r } = parseLabelSub(node.labelSubText || "");
      return node.label?.toLowerCase().includes(t) || team.toLowerCase().includes(t) || r.toLowerCase().includes(t);
    };
    const filterTree = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map((node) => {
        const isLeaf = !node.children || node.children.length === 0;
        if (isLeaf) return match(node) ? node : null;
        const filtered = filterTree(node.children);
        if (filtered.length > 0) return { ...node, children: filtered };
        return match(node) ? { ...node, children: [] } : null;
      }).filter(Boolean) as TreeNode[];
    };
    setExpandedLeft(collectBranchKeys(treeData));
    return filterTree(treeData);
  }, [treeData, searchText]);

  const totalPeople = allUsers.length;
  const rightCount = useMemo(() => {
    let cnt = 0;
    const walk = (nodes: TeamNode[]) => {
      for (const n of nodes) {
        if ((n as any)._isUser) cnt++;
        else if (n.children) walk(n.children);
      }
    };
    walk(rightTreeData);
    return cnt;
  }, [rightTreeData]);

  return (
    <div style={st.page}>
      {showHeader && (
        <div style={st.header}>
          <div style={st.headerLeft}>
            <div style={st.logo}><IconUser style={{ fontSize: 18, color: "#fff" }} /></div>
            <div>
              <div style={st.title}>通讯录</div>
              <div style={st.sub}>{showAll ? "全公司" : "我的团队"} · {totalPeople} 人</div>
            </div>
          </div>
          <Input
            prefix={<IconSearch style={{ color: "var(--semi-color-text-3)" }} />}
            placeholder="搜索姓名、团队、角色..."
            value={searchText}
            onChange={setSearchText}
            showClear
            style={{ width: 280 }}
          />
          <Button
            type={showAll ? "primary" : "tertiary"}
            icon={<IconGlobe />}
            onClick={() => { setShowAll(!showAll); setSearchText(""); }}
            style={{ borderRadius: 20, flexShrink: 0 }}
          >
            {showAll ? "我的团队" : "查看全公司"}
          </Button>
        </div>
      )}

      <div style={st.body}>
        <div style={{ ...st.sidebar, width: leftWidth }}>
          <div style={st.panelHead}><span>上下级关系</span></div>
          <div style={st.treeWrap}>
            {loading ? (
              <div style={st.loadingBox}><Spin /></div>
            ) : filteredLeftTree.length === 0 ? (
              <div style={st.emptySmall}>{searchText ? "无匹配结果" : "暂无数据"}</div>
            ) : (
              <Tree
                treeData={filteredLeftTree}
                expandedKeys={expandedLeft}
                onExpand={(keys) => setExpandedLeft(keys as string[])}
                renderLabel={(_, treeNode) => {
                  const node = treeNode as any;
                  const isLeaf = !node.children || node.children.length === 0;
                  const { team, role: r } = parseLabelSub(node.labelSubText || "");
                  const cnt = countAll(node);
                  return (
                    <div style={st.nodeRow}>
                      <span style={{ ...st.nodeName, fontWeight: isLeaf ? 500 : 700 }}>{node.label}</span>
                      {team && <span style={st.nodeTeam}>{team}</span>}
                      {r && <Tag size="small" color={getRoleColor(r) as any} style={st.nodeTag}>{r}</Tag>}
                      {cnt > 1 && <span style={st.nodeCnt}>{cnt}人</span>}
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>

        <div style={st.main}>
          <div style={st.panelHead}>
            <div style={st.panelHeadLeft}>
              <IconList style={{ fontSize: 14, color: "var(--semi-color-text-3)" }} />
              <span>部门通讯录</span>
            </div>
            <Tag size="small" style={{ margin: 0 }}>{rightCount} 人</Tag>
          </div>
          <div style={st.mainContent}>
            {rightTreeData.length === 0 ? (
              <div style={st.emptyLarge}>
                <IconSearch style={{ fontSize: 48, color: "var(--semi-color-text-4)", marginBottom: 12 }} />
                <div style={{ fontSize: 15, color: "var(--semi-color-text-2)" }}>{searchText ? "无匹配结果" : "暂无数据"}</div>
              </div>
            ) : (
              <Tree
                treeData={rightTreeData}
                expandedKeys={expandedRight}
                onExpand={(keys) => setExpandedRight(keys as string[])}
                renderLabel={(_, treeNode) => {
                  const node = treeNode as any;
                  if (node._isUser) {
                    const roleName = node.labelExt || "";
                    return (
                      <div style={st.userRow}>
                        <div style={{ ...st.userAvatar, background: getAvatarColor(node.label) }}>{node.label?.charAt(0)}</div>
                        <span style={st.userName}>{node.label}</span>
                        {roleName && <Tag size="small" color={getRoleColor(roleName) as any} style={st.nodeTag}>{roleName}</Tag>}
                        <Tooltip content="复制姓名">
                          <div style={st.copyBtn} onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(node.label).then(() => Toast.success({ content: "已复制", duration: 2 })); }}>
                            <IconCopy style={{ fontSize: 12 }} />
                          </div>
                        </Tooltip>
                      </div>
                    );
                  }
                  const cnt = countUsers(node);
                  return (
                    <div style={st.deptRow}>
                      <span style={st.deptName}>{node.label}</span>
                      <span style={st.deptCnt}>{cnt}</span>
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ 样式 ============

const st: Record<string, React.CSSProperties> = {
  page: { display: "flex", flexDirection: "column", height: "100%", background: "#f7f7fa" },
  header: { display: "flex", alignItems: "center", padding: "6px 12px", background: "#fff", borderBottom: "1px solid var(--semi-color-border)", gap: 12, flexShrink: 0 },
  headerLeft: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },
  logo: { width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 10px rgba(99,102,241,0.3)" },
  title: { fontSize: 16, fontWeight: 700, color: "var(--semi-color-text-0)", lineHeight: 1.2 },
  sub: { fontSize: 11, color: "var(--semi-color-text-3)", marginTop: 1 },
  body: { display: "flex", flex: 1, overflow: "hidden" },
  panelHead: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", fontSize: 12, fontWeight: 600, color: "var(--semi-color-text-3)", borderBottom: "1px solid var(--semi-color-border)", textTransform: "uppercase" as const, letterSpacing: 1, flexShrink: 0 },
  panelHeadLeft: { display: "flex", alignItems: "center", gap: 6 },
  sidebar: { background: "#fff", borderRight: "1px solid var(--semi-color-border)", display: "flex", flexDirection: "column", flexShrink: 0 },
  treeWrap: { flex: 1, overflow: "auto", padding: "4px" },
  loadingBox: { display: "flex", justifyContent: "center", padding: 60 },
  emptySmall: { textAlign: "center" as const, padding: 40, color: "var(--semi-color-text-3)", fontSize: 13 },
  nodeRow: { display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 0" },
  nodeName: { fontSize: 13, color: "var(--semi-color-text-0)" },
  nodeTeam: { fontSize: 10, color: "#6b7280", background: "#f3f4f6", borderRadius: 3, padding: "0 5px", lineHeight: "16px", whiteSpace: "nowrap" as const },
  nodeTag: { margin: 0, fontSize: 10, lineHeight: "16px" },
  nodeCnt: { fontSize: 11, color: "#9ca3af", marginLeft: 2 },
  main: { flex: 1, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" },
  mainContent: { flex: 1, overflow: "auto", padding: "4px 8px" },
  deptRow: { display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 0" },
  deptName: { fontSize: 13, fontWeight: 700, color: "var(--semi-color-text-0)" },
  deptCnt: { fontSize: 10, color: "#6b7280", background: "#f0f0ff", borderRadius: 10, padding: "0 6px", lineHeight: "16px", fontWeight: 600 },
  userRow: { display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 0" },
  userAvatar: { width: 24, height: 24, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 },
  userName: { fontSize: 13, fontWeight: 500, color: "var(--semi-color-text-0)" },
  copyBtn: { width: 20, height: 20, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--semi-color-text-3)", flexShrink: 0, transition: "all 0.15s", marginLeft: 2 },
  emptyLarge: { display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center", padding: "80px 0" },
};
